<template>
  <content-container title="申请记录" :table-row-count="tableData.length" style="height: calc(100% - 76px);">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.approvalNo" placeholder="审批单编号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.invoiceSubjectId"
          placeholder="开票科目"
          clearable
        >
          <el-option
            v-for="item in recordSubjectList"
            :key="item.invoiceSubjectId"
            :label="item.invoiceSubjectName"
            :value="item.invoiceSubjectId"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.approvalStatus"
          placeholder="审批状态"
          clearable
        >
          <el-option
            v-for="item in approvalStatusList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </search-col>
      <template v-if="source === 'sass-match'">
        <search-col>
          <el-input v-model="params.companyName" placeholder="企业名称" clearable @keydown="onEnter" />
        </search-col>
        <search-col>
          <el-input v-model="params.superagentName" placeholder="特代名称" clearable @keydown="onEnter" />
        </search-col>
      </template>
     
      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData">
      <el-table-column align="left" type="index" label="序号" :min-width="80" />
      <el-table-column align="left" prop="approvalNo" show-overflow-tooltip label="审批单编号" :min-width="200" :formatter="(row) => isNull(row.approvalNo) ? '--' : row.approvalNo" />
      <el-table-column align="left" show-overflow-tooltip prop="invoiceSubjectName" label="开票科目" :min-width="280" :formatter="(row) => isNull(row.invoiceSubjectName) ? '--' : row.invoiceSubjectName" />
      <el-table-column align="right" prop="orderCount" label="合并子订单数" :min-width="120" />
      <el-table-column align="right" show-overflow-tooltip prop="orderAmount" label="合并订单金额" :min-width="150" />
      <template v-if="source === 'sass-match'">
        <el-table-column align="left" show-overflow-tooltip prop="companyName" label="企业名称" :min-width="260" :formatter="(row) => isNull(row.companyName) ? '--' : row.companyName" />
        <el-table-column align="left" show-overflow-tooltip prop="superagentName" label="特代名称" :min-width="260" :formatter="(row) => isNull(row.superagentName) ? '--' : row.superagentName" />
      </template>
      <el-table-column align="left" prop="submissionTime" label="提交时间" :min-width="180" :formatter="(row) => isNull(row.submissionTime) ? '--' : row.submissionTime" />
      <el-table-column align="left" prop="approvalTime" label="审批日期" :min-width="180" :formatter="(row) => isNull(row.approvalTime) ? '--' : row.approvalTime" />
      <el-table-column align="center" prop="approvalStatus" label="审批状态" :min-width="100">
        <template #default="scope">
          <custom-tag :label="invoiceApplyRecordStatusLabel[scope.row.approvalStatus]" :type="invoiceApplyRecordStatusStyle[scope.row.approvalStatus]" />
        </template>
      </el-table-column>
      <el-table-column align="left" show-overflow-tooltip prop="approvalNotes" label="审批备注" :min-width="150" :formatter="(row) => isNull(row.approvalNotes) ? '--' : row.approvalNotes" />
      <el-table-column
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        :min-width="100"
      >
        <template #default="scope">
          <div class="flex justify-center">
            <el-button type="primary" link @click="handleDetail(scope.row)">
              详情
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { reactive, ref, PropType, watch, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, CustomTag } from '@vue3/components';
import {
  ContentContainer, 
  SearchContainer, 
  SearchCol,
  queryInvoiceApplyRecord,
  queryRecordSubjectList, 
} from '@core/main';
import { EApproveType, EHnInvoiceType, IEnterpriseItem, IServerOptionsItem } from '@core/main/types/common';
import {
  IInvoiceApplyRecordItem, 
  IInvoiceApplyRecordParams, 
  invoiceApplyRecordStatusLabel, 
  invoiceApplyRecordStatusStyle,
  IWarningOrdersList, 
} from '@core/main/types/crowd-source/invoice-pay-taxes';
import { isNull } from '@core/tools';
import { useRouter } from 'vue-router';

defineOptions({ name: 'ApplicationRecord' }); // 众包灵工 - 申请记录

const props = defineProps({
  source: {
    type: String as PropType<'server' | 'company' | 'sass-crowd' | 'sass-match'>,
    default: 'server',
  },
  companyList: {
    type: Array as PropType<IEnterpriseItem[]>,
    default: () => [],
  },
  serverList: {
    type: Array as PropType<IServerOptionsItem[]>,
    default: () => [],
  },
  isPermission: {
    type: Boolean,
    default: false,
  },
});

const defaultPageSize = 12;

const router = useRouter();

const isCrowd = computed(() => props.source === 'server' || props.source === 'sass-crowd');

const params = reactive<IInvoiceApplyRecordParams>({
  page: 1,
  size: defaultPageSize,
  businessType: isCrowd.value ? 'SERVER' : 'COMPANY',
});
const loading = ref(false);
const total = ref(0);
const tableData = ref([] as IInvoiceApplyRecordItem[]);
const approvalStatusList = ref(
  Object.values(invoiceApplyRecordStatusLabel).map((item) => ({
    label: invoiceApplyRecordStatusLabel[item],
    value: item,
  })),
);
const recordSubjectList = ref([] as IWarningOrdersList[]);

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryInvoiceApplyRecord(params);

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function handleDetail(row: IInvoiceApplyRecordItem) {
  router.push({
    name: isCrowd.value ? 'CrowdSourceRecordDetail' : 'BrokerRecordDetail',
    query: {
      id: row?.id,
      type: isCrowd.value ? row?.invoiceApplyChannel === EHnInvoiceType.OFFLINE ? EApproveType.CROWD_TAX_OFFLINE_INVOICE : EApproveType.CROWD_TAX_INVOICE : EApproveType.MATCH_TAX_INVOICE,
      source: props.source,
    },
  });
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

async function fetchRecordSubjectList() {
  try {
    recordSubjectList.value = await queryRecordSubjectList({
      businessId: props.source === 'server' ? props.serverList?.[0]?.id : props.source === 'company' ? props.companyList?.[0]?.id : undefined,
      businessType: params.businessType, 
    });
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

watch(
  () => props.isPermission,
  (val) => {
    if (!val) {
      fetchRecordSubjectList();
      fetchData();
    }
  },
  { immediate: true },
);
</script>
